import React, { useState, useEffect } from 'react';
import { Card, Form, Select, Input, Switch, Button, Alert, Row, Col, Divider, message, Tag } from 'antd';
import { BankOutlined, CheckCircleOutlined } from '@ant-design/icons';

const { Option } = Select;

interface Props { data?: any; onRefresh: () => void; }

const PaymentMethodSettings: React.FC<Props> = ({ onRefresh }) => {
  const [form] = Form.useForm();
  const [method, setMethod] = useState('paystack');
  const [loading, setLoading] = useState(false);
  const tenantId = localStorage.getItem('hr_tenant_id') || 'superadmin';

  useEffect(() => {
    fetch(`https://nexus-hr.talenthubupskilling.com/backend/hr-api/settings/settings.php?tenant_id=${tenantId}&action=system_config`)
      .then(r => r.json()).then(d => {
        if (d.success && d.data) {
          const m = d.data.payment_method || 'paystack';
          setMethod(m);
          form.setFieldsValue({
            payment_method: m,
            manual_payment_bank: d.data.manual_payment_bank || '',
            paystack_public_key: d.data.paystack_public_key || '',
            paystack_secret_key: d.data.paystack_secret_key || '',
          });
        }
      }).catch(() => {});
  }, []);

  const handleSave = async (values: any) => {
    setLoading(true);
    try {
      const tenantId = localStorage.getItem('hr_tenant_id') || 'superadmin';
      // Only save Paystack keys if method is paystack AND keys provided
      if (values.payment_method === 'paystack' && values.paystack_public_key && values.paystack_secret_key) {
        await fetch(`https://nexus-hr.talenthubupskilling.com/backend/hr-api/settings/settings.php?tenant_id=${tenantId}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'save_paystack', public_key: values.paystack_public_key, secret_key: values.paystack_secret_key, test_mode: '0', tenant_id: tenantId })
        });
      }
      // Save payment method directly to nexus_hr_settings
      await fetch(`https://nexus-hr.talenthubupskilling.com/backend/hr-api/settings/settings.php?tenant_id=${tenantId}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          action: 'save_payment_method',
          payment_method: values.payment_method,
          manual_payment_bank: values.manual_payment_bank || '',
          tenant_id: tenantId 
        })
      });
      message.success('Payment settings saved');
      onRefresh();
    } finally { setLoading(false); }
  };

  return (
    <div>
      <Alert message="Choose how salaries are paid out. Paystack enables automated bank transfers. Manual mode lets HR record payments made outside the system." type="info" showIcon style={{ marginBottom: 16 }} />
      <Card title="Salary Payment Method">
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item name="payment_method" label="Payment Method" rules={[{ required: true }]}>
            <Select onChange={v => setMethod(v)} size="large">
              <Option value="paystack"><Tag color="green">Recommended</Tag> Paystack — Automated Bank Transfer</Option>
              <Option value="manual"><Tag color="blue">Manual</Tag> Manual Bank Transfer — HR records reference numbers</Option>
              <Option value="cash"><Tag color="orange">Cash</Tag> Cash Payment — HR marks as paid directly</Option>
              <Option value="cheque"><Tag color="purple">Cheque</Tag> Cheque Payment</Option>
            </Select>
          </Form.Item>

          {method === 'paystack' && (
            <>
              <Divider>Paystack API Keys</Divider>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="paystack_public_key" label="Public Key">
                    <Input placeholder="pk_live_..." />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="paystack_secret_key" label="Secret Key">
                    <Input.Password placeholder="sk_live_..." />
                  </Form.Item>
                </Col>
              </Row>
              <Alert message="Paystack transfers funds directly to employee bank accounts. Ensure employees have bank details configured in their payroll records." type="success" showIcon />
            </>
          )}

          {method === 'manual' && (
            <>
              <Divider>Manual Transfer Settings</Divider>
              <Form.Item name="manual_payment_bank" label="Company Bank Account (for reference on payslips)">
                <Input prefix={<BankOutlined />} placeholder="e.g. FCMB — 6592148013 — Etfash Environmental Services" />
              </Form.Item>
              <Alert message="In manual mode, HR approves payroll then records the bank transfer reference number when payment is made. The system marks the record as paid and sends the payslip email." type="info" showIcon />
            </>
          )}

          {(method === 'cash' || method === 'cheque') && (
            <Alert message={`In ${method} mode, HR approves payroll and marks records as paid directly. Payslip emails are sent automatically.`} type="info" showIcon />
          )}

          <Divider />
          <Button type="primary" htmlType="submit" loading={loading} icon={<CheckCircleOutlined />}>
            Save Payment Settings
          </Button>
        </Form>
      </Card>
    </div>
  );
};

export default PaymentMethodSettings;
